import axios from 'utils/axios';
import { CatalogProduct } from '../types/products';

export type ProductsListResponse = {
  message: string;
  data: CatalogProduct[];
};

export type CreateProductPayload = {
  preferenceCode?: string;
  code?: string;
  newCode?: string;
  color?: string;
  description?: string;
  system?: string;
  ncm?: string;
  packagingUnit?: number;
  unit?: 'Jogo' | 'm' | 'pç' | 'set' | 'St';
  fraction?: number;
  fullNetPrice?: number;
  ipi?: number;
  unitWeight?: number;
  productGroup?: number;
};

/**
 * Lista todos os produtos cadastrados no catálogo
 */
export async function listProducts(): Promise<CatalogProduct[]> {
  const { data } = await axios.get<ProductsListResponse>('/products');
  return data.data || [];
}

/**
 * Cria um novo produto no catálogo
 */
export async function createProduct(payload: CreateProductPayload): Promise<CatalogProduct> {
  const { data } = await axios.post<{ message: string; data: CatalogProduct }>('/products', payload);
  return data.data;
}

/**
 * Busca um produto pelo ID
 */
export async function getProduct(id: string): Promise<CatalogProduct> {
  const { data } = await axios.get<{ message: string; data: CatalogProduct }>(`/products/${id}`);
  return data.data;
}

/**
 * Atualiza um produto existente do catálogo
 */
export async function updateProduct(id: string, payload: CreateProductPayload): Promise<CatalogProduct> {
  const { data } = await axios.patch<{ message: string; data: CatalogProduct }>(`/products/${id}`, payload);
  return data.data;
}

/**
 * Remove um produto do catálogo (soft delete)
 */
export async function deleteProduct(id: string): Promise<void> {
  await axios.delete(`/products/${id}`);
}

export type SheetProductRequest = {
  code: string;
  description?: string;
  ncm?: string;
  unit?: string;
  packagingUnit?: number;
  fraction?: number;
  ipi?: number;
  unitWeight?: number;
  /** Preço de tabela (já sem o divisor de cliente final). */
  price?: number;
};

export type SheetProductResult = {
  code: string;
  status:
    | 'exists'
    | 'will_create'
    | 'created'
    | 'not_in_omie'
    | 'inactive_in_omie'
    | 'no_price'
    | 'omie_id_in_use'
    | 'error';
  message: string;
  description?: string;
  price?: number;
  productId?: string;
};

/**
 * Cadastro automático de produtos de uma planilha que ainda não existem no catálogo: procura no
 * Omie (só leitura) e, existindo lá, cria com a identidade do Omie + preço/IPI/embalagem da
 * planilha. `dryRun` só simula.
 */
export async function resolveProductsFromSheet(
  items: SheetProductRequest[],
  dryRun: boolean,
): Promise<SheetProductResult[]> {
  const { data } = await axios.post<{ data: SheetProductResult[] }>('/products/from-sheet', { items, dryRun });
  return data.data;
}
